import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Trash2, Calendar, MapPin, Phone, Ban, Clock } from "lucide-react";
import DriverBottomNav from "@/components/DriverBottomNav";
import { fmt12 } from "@/lib/time";
import { toast } from "sonner";

import { API } from "@/lib/api";


// 6:00 AM – 10:30 PM in 30-min steps (covers normal driver hours)
const TIME_OPTIONS = [];
for (let h = 6; h <= 22; h++) {
  TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:00`);
  TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:30`);
}

// Group bookings by scheduled_date for the agenda view
const groupByDate = (items) => {
  const map = new Map();
  for (const b of items) {
    const k = b.scheduled_date || "TBD";
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(b);
  }
  // Sort dates ascending (today → future), and within each date sort by
  // scheduled_time ascending so a driver can read their day top-to-bottom
  // in chronological order.
  for (const [, bookings] of map) {
    bookings.sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  }
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
};

const prettyDate = (iso) => {
  if (!iso || iso === "TBD") return "Unscheduled";
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate()+1);
  if (d.toDateString() === today.toDateString()) return `Today · ${d.toLocaleDateString(undefined, { weekday:"short", month:"short", day:"numeric" })}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow · ${d.toLocaleDateString(undefined, { weekday:"short", month:"short", day:"numeric" })}`;
  return d.toLocaleDateString(undefined, { weekday:"long", month:"short", day:"numeric" });
};

const endTime = (start, miles) => {
  try {
    const [hh, mm] = start.split(":").map(Number);
    const dur = Math.max(30, Math.round((miles || 0) * 2) + 15);
    const total = hh * 60 + mm + dur;
    return `${String(Math.floor(total/60) % 24).padStart(2,'0')}:${String(total % 60).padStart(2,'0')}`;
  } catch { return start; }
};

export default function Schedule() {
  const navigate = useNavigate();
  const [agenda, setAgenda] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockDraft, setBlockDraft] = useState({ date: "", start: "09:00", end: "10:00", reason: "" });

  const fetchAll = useCallback(async () => {
    // Fire requests independently so one 404 doesn't break the others.
    setLoading(true);
    try {
      const a = await axios.get(`${API}/driver/agenda`);
      setAgenda(a.data.items || []);
    } catch (e) {
      console.error("Failed to fetch agenda:", e);
    }
    try {
      const tb = await axios.get(`${API}/driver/time-blocks`);
      setBlocks(tb.data.items || []);
    } catch (e) {
      console.error("Failed to fetch time blocks:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveBlock = async () => {
    if (!blockDraft.date) { toast.error("Pick a date"); return; }
    if (blockDraft.end <= blockDraft.start) { toast.error("End time must be after start"); return; }
    try {
      await axios.post(`${API}/driver/time-blocks`, blockDraft);
      toast.success("Personal time blocked");
      setShowBlockForm(false);
      setBlockDraft({ date: "", start: "09:00", end: "10:00", reason: "" });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add block");
    }
  };

  const deleteBlock = async (id) => {
    try {
      await axios.delete(`${API}/driver/time-blocks/${id}`);
      toast.success("Block removed");
      fetchAll();
    } catch {
      toast.error("Failed to remove block");
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-[#050505]">
      <header className="flex-shrink-0 z-10 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="px-5 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/driver/settings")} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white">Calendar</h1>
            <p className="text-sm text-zinc-500">Upcoming bookings and personal time off</p>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-6 pb-24 space-y-4">
        {/* Upcoming bookings agenda — driver's calendar of assigned/available trips */}
        <section data-testid="agenda-section">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-[#4D88FF]" />
            <h2 className="text-xs uppercase tracking-wider text-zinc-400 font-bold">
              Upcoming bookings
              {agenda.length > 0 && (
                <span className="ml-2 text-[#4D88FF]" data-testid="agenda-count">{agenda.length}</span>
              )}
            </h2>
          </div>
          {loading ? null : agenda.length === 0 ? (
            <Card className="bg-[#121212] border-white/5">
              <CardContent className="py-6 text-center text-sm text-zinc-500">
                No upcoming bookings on your calendar
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {groupByDate(agenda).map(([dateKey, items]) => (
                <div key={dateKey} data-testid={`agenda-day-${dateKey}`}>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 px-1">
                    {prettyDate(dateKey)}
                  </p>
                  <div className="space-y-2">
                    {items.map((b) => (
                      <Card
                        key={b.id}
                        className="bg-[#121212] border-white/5 hover:border-white/10 cursor-pointer transition-colors"
                        onClick={() => navigate(`/driver/track/${b.id}`)}
                        data-testid={`agenda-card-${b.id}`}
                      >
                        <CardContent className="p-3 space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-sm font-bold text-white">
                              <Clock className="w-3.5 h-3.5 text-[#4D88FF]" />
                              {fmt12(b.scheduled_time)} <span className="text-zinc-500 font-normal">→ ~{fmt12(endTime(b.scheduled_time, b.distance_miles))}</span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap justify-end">
                              {b.asap ? (
                                <Badge
                                  className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 text-[10px] font-bold uppercase"
                                  data-testid={`agenda-pill-asap-${b.id}`}
                                >
                                  ASAP
                                </Badge>
                              ) : (
                                <Badge
                                  className="bg-sky-500/15 text-sky-300 border-sky-500/30 text-[10px] font-bold uppercase"
                                  data-testid={`agenda-pill-scheduled-${b.id}`}
                                >
                                  Scheduled
                                </Badge>
                              )}
                              {!b.driver_id && (
                                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] font-bold uppercase">Unclaimed</Badge>
                              )}
                              <Badge className={`text-[10px] font-bold uppercase ${
                                b.status === 'confirmed' ? 'bg-[#0047FF]/15 text-[#4D88FF] border-[#0047FF]/30' :
                                b.status === 'in_progress' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
                                'bg-white/5 text-white/60 border-white/10'
                              }`}>
                                {b.status}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-xs text-zinc-300 truncate flex items-center gap-1.5">
                            <MapPin className="w-3 h-3 text-emerald-400 shrink-0" />
                            {b.pickup_address}
                          </div>
                          {Array.isArray(b.stops) && b.stops.length > 0 && (
                            <div className="text-xs text-amber-300 truncate flex items-center gap-1.5">
                              <MapPin className="w-3 h-3 shrink-0" />
                              {b.stops.length} {b.stops.length === 1 ? "stop" : "stops"}
                            </div>
                          )}
                          <div className="text-xs text-zinc-400 truncate flex items-center gap-1.5">
                            <MapPin className="w-3 h-3 text-[#4D88FF] shrink-0" />
                            {b.dropoff_address}
                          </div>
                          <div className="flex items-center justify-between pt-1 text-[11px]">
                            <span className="text-zinc-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {b.customer_name}
                            </span>
                            <span className="font-bold text-white">${b.estimated_price?.toFixed(2)}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Personal time blocks — driver-blocked windows that count as occupied */}
        <section data-testid="blocks-section" className="pt-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4 text-amber-400" />
              <h2 className="text-xs uppercase tracking-wider text-zinc-400 font-bold">
                Personal time off
                {blocks.length > 0 && <span className="ml-2 text-amber-300" data-testid="blocks-count">{blocks.length}</span>}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowBlockForm((v) => !v)}
              data-testid="toggle-block-form"
              className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 h-7"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> {showBlockForm ? "Cancel" : "Block off time"}
            </Button>
          </div>
          {showBlockForm && (
            <Card className="bg-amber-500/5 border-amber-500/30 mb-3" data-testid="block-form">
              <CardContent className="p-3 space-y-2">
                <input
                  type="date"
                  value={blockDraft.date}
                  min={new Date().toISOString().slice(0,10)}
                  onChange={(e) => setBlockDraft({ ...blockDraft, date: e.target.value })}
                  className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="block-date"
                />
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={blockDraft.start}
                    onChange={(e) => setBlockDraft({ ...blockDraft, start: e.target.value })}
                    className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    data-testid="block-start"
                  >
                    {TIME_OPTIONS.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
                  </select>
                  <select
                    value={blockDraft.end}
                    onChange={(e) => setBlockDraft({ ...blockDraft, end: e.target.value })}
                    className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    data-testid="block-end"
                  >
                    {TIME_OPTIONS.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
                  </select>
                </div>
                <input
                  type="text"
                  value={blockDraft.reason}
                  placeholder="Reason (optional) — e.g. doctor appt"
                  onChange={(e) => setBlockDraft({ ...blockDraft, reason: e.target.value })}
                  className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="block-reason"
                />
                <Button
                  onClick={saveBlock}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold h-9"
                  data-testid="block-save"
                >
                  Save block
                </Button>
              </CardContent>
            </Card>
          )}
          {blocks.length > 0 && (
            <div className="space-y-1.5">
              {blocks.map((b) => (
                <Card key={b.id} className="bg-[#121212] border-amber-500/20" data-testid={`block-${b.id}`}>
                  <CardContent className="p-3 flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm text-white font-semibold">
                        {prettyDate(b.date)} · {fmt12(b.start)} – {fmt12(b.end)}
                      </p>
                      {b.reason && (
                        <p className="text-xs text-amber-300/80 mt-0.5">{b.reason}</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteBlock(b.id)}
                      className="text-white/40 hover:text-red-400 p-1"
                      data-testid={`block-delete-${b.id}`}
                      aria-label="Remove block"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>

      <DriverBottomNav />
    </div>
  );
}
